import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";


const AuthContext = createContext(null);


export const getDefaultRoute = (user) => {
  if (!user) return "/login";
  if (user.role === "super_admin") return "/platform/dashboard";
  if (user.role === "company_admin") return "/company/dashboard";
  if (user.role === "scanner_admin") return "/scanner/scan";
  if (user.role === "manager") return "/manager/dashboard";
  return "/employee/dashboard";
};


export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("pipeforge_token"));
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [featureFlags, setFeatureFlags] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [scannerLocation, setScannerLocation] = useState(null);
  const [leaveSettings, setLeaveSettings] = useState(null);
  const [leaveEnabled, setLeaveEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const clearAuth = () => {
    localStorage.removeItem("pipeforge_token");
    setToken(null);
    setUser(null);
    setTenant(null);
    setFeatureFlags(null);
    setEmployee(null);
    setScannerLocation(null);
    setLeaveSettings(null);
    setLeaveEnabled(false);
  };

  const loadProfile = async () => {
    const response = await api.get("/auth/me");
    setUser(response.data.user);
    setTenant(response.data.tenant);
    setFeatureFlags(response.data.feature_flags);
    setEmployee(response.data.employee);
    setScannerLocation(response.data.scanner_location || null);
    setLeaveSettings(response.data.leave_settings || null);
    setLeaveEnabled(Boolean(response.data.leave_enabled));
    return response.data;
  };

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    if (response.data.requires_password_reset) {
      clearAuth();
      return response.data;
    }
    localStorage.setItem("pipeforge_token", response.data.access_token);
    setToken(response.data.access_token);
    return loadProfile();
  };

  const logout = () => {
    clearAuth();
  };

  useEffect(() => {
    const bootstrap = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        await loadProfile();
      } catch {
        clearAuth();
      } finally {
        setLoading(false);
      }
    };
    bootstrap();
  }, [token]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(
    () => ({
      token,
      user,
      tenant,
      featureFlags,
      employee,
      scannerLocation,
      leaveSettings,
      leaveEnabled,
      loading,
      login,
      logout,
      refreshProfile: loadProfile,
    }),
    [token, user, tenant, featureFlags, employee, scannerLocation, leaveSettings, leaveEnabled, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
};